import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useImages, useNearViewport } from '../hooks/useImage';

const CollageItem = ({ imageUrl, size, pos, rotate }) => {
  const [itemRef, isNear] = useNearViewport("400px");
  const shouldLoad = Boolean(imageUrl) && isNear;

  return (
    <div
      ref={itemRef}
      className={`absolute ${pos} ${size} cursor-pointer group`}
      style={{ rotate: `${rotate}deg` }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        whileHover={{ 
          scale: 1.05, 
          rotate: 0, 
          zIndex: 100,
          transition: { duration: 0.2 } 
        }}
        viewport={{ once: true, amount: 0.2 }}
        className="h-full w-full"
      >
        <div className="h-full w-full overflow-hidden rounded-xl md:rounded-2xl border-[5px] md:border-[10px] border-white bg-white shadow-xl ring-1 ring-black/5">
          {shouldLoad ? (
            <img
              src={imageUrl}
              alt="Animal gallery"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 animate-pulse" />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default function HomeCollage() {
  const imageIds = useMemo(() => [
    "RzRjxZhTmWXcJYN6Fd3v", "kSBZbG2bIuahV6x0OLPE", "VwQQ5LlZmLgwi1EHTrpG",
    "WZODBIlLzl4Q8eCw3Bdd", "zqlBHPsQ6XoISdFihxeS", "w70JgNgBBYhr2iSX6WrD",
    "C3P9jXGmh1HCACZ1hecZ", "E7Flx2pxtkCWkPO5Y4sC", "8hNne3xK86EW15OsU75r",
    "PKkZ9e4MlI8qI3Crn6Bi"
  ], []);

  const { imagesById } = useImages(imageIds);

  const layoutSlots = [
    { size: "w-40 h-52 md:w-64 md:h-80", pos: "top-[5%] left-[5%] md:top-[5%] md:left-[2%]", rotate: -6 },
    { size: "w-36 h-36 md:w-60 md:h-60", pos: "top-[2%] left-[55%] md:top-[2%] md:left-[35%]", rotate: 4 },
    { size: "w-40 h-52 md:w-64 md:h-80", pos: "top-[18%] left-[10%] md:top-[8%] md:left-[65%]", rotate: -3 },
    { size: "w-32 h-32 md:w-56 md:h-56", pos: "top-[35%] left-[50%] md:top-[35%] md:left-[12%]", rotate: 8 },
    { size: "w-52 h-40 md:w-80 md:h-60", pos: "top-[45%] left-[5%] md:top-[30%] md:left-[40%]", rotate: -2 },
    { size: "w-40 h-52 md:w-56 md:h-72", pos: "top-[55%] left-[50%] md:top-[32%] md:left-[75%]", rotate: 5 },
    { size: "w-44 h-44 md:w-64 md:h-64", pos: "top-[70%] left-[10%] md:top-[65%] md:left-[5%]", rotate: -8 },
    { size: "w-40 h-52 md:w-60 md:h-72", pos: "top-[75%] left-[50%] md:top-[60%] md:left-[30%]", rotate: 10 },
    { size: "w-52 h-40 md:w-72 md:h-56", pos: "top-[88%] left-[5%] md:top-[68%] md:left-[55%]", rotate: -4 },
    { size: "w-32 h-32 md:w-52 md:h-52", pos: "top-[90%] left-[60%] md:top-[62%] md:left-[82%]", rotate: 7 },
  ];

  const randomPaws = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    top: `${Math.random() * 95}%`,
    left: `${Math.random() * 95}%`,
    rotate: Math.random() * 360,
    delay: i * 0.5
  }));

  return (
    <section className="bg-custom-light-orange py-16 md:py-32 overflow-hidden relative min-h-[1200px] md:min-h-[1300px]">
      
      {/* 🐾 BRIGHT INFINITE PAWS */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {randomPaws.map((paw) => (
          <motion.div
            key={paw.id}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ 
              opacity: [0, 1, 1, 0], 
              scale: [0.8, 1.2, 1.2, 0.8],
            }}
            transition={{ 
              duration: 5, 
              repeat: Infinity, 
              delay: paw.delay,
              ease: "easeInOut"
            }}
            className="absolute text-white filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
            style={{ top: paw.top, left: paw.left, rotate: `${paw.rotate}deg` }}
          >
            <svg width="35" height="35" className="md:w-[55px] md:h-[55px]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,5c-0.8,0-1.5,0.7-1.5,1.5S11.2,8,12,8s1.5-0.7,1.5-1.5S12.8,5,12,5z M7.5,7c-0.8,0-1.5,0.7-1.5,1.5S6.7,10,7.5,10 S9,9.3,9,8.5S8.3,7,7.5,7z M16.5,7c-0.8,0-1.5,0.7-1.5,1.5S15.7,10,16.5,10S18,9.3,18,8.5S17.3,7,16.5,7z M12,10 c-3.3,0-6,2.7-6,6c0,2.2,1.8,4,4,4s4-1.8,4-4C14,13.8,13.1,11.8,12,10z"/>
            </svg>
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto relative z-10 h-full">
        {/* 🖼️ DYNAMIC SCATTERED COLLAGE */}
        <div className="relative w-full h-[1000px] md:h-[1100px]">
          {imageIds.map((docId, index) => (
            <CollageItem
              key={docId}
              imageUrl={imagesById[docId]}
              {...layoutSlots[index]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
